import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Stack,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useOutletContext } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";

import S4ReasonTable from "../EndUser/S4ReasonTable";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { ConditionalWrapper } from "../utils/ConditionalWrapper";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PopupWithCheckboxes from "../EndUser/PopupWithCheckboxes";
import { Choice } from "../utils/PayloadBuilder";
import { useSnackbar } from "../SnakBar/AppSnackbarProvider";
import ButtonComponent from "../common/button/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedRow,
  setSelectedDropdownValue,
} from "../../redux/actions/supportTicket";
import Download from "../common/DownloadGrid/Download";
import { setSubmittedData } from "../../redux/actions/userSlice";
import VarianceFilter from "../common/VarianceFilter/VarianceFilter";
import type { VarianceFilterState } from "../common/VarianceFilter/VarianceFilter";
import BulkPasteFilter from "../common/BulkFilter/BulkFilter";
import FilterManagerButton from "../common/FilterVariant/FilterManagerButton";
/* -------------------------- Outlet Context Types -------------------------- */

export type EndUserOutletContext = {
  gridRef: any;
  gridTheme: any;
  rowsPerPage: number;
  setGridReady: (b: boolean) => void;
  sideBar: any;
  loading: boolean;

  columns: any[];
  outputColumns: any[];
  inputRows: any[];
  approvedRows: any[];
  rejectedRows: any[];
  approvedBothRows: any[];
  dataErrorRows: any[];
  selectedItemsdata: any[];

  setSelectedHistoryRow: (row: any) => void;
  setOpenHistory: (open: boolean) => void;

  handleGridChange: (data?: any) => void;
  onRowSelected: (row?: any) => void;

  handleHeaderTakeActionClick: () => void;
  handleSubmitClick: () => void;
  canSubmitPlanner: boolean;
};

export function useEndUserOutletContext() {
  return useOutletContext<EndUserOutletContext>();
}

/* ------------------------------ RenderTable ------------------------------- */

type RenderTableProps = {
  gridRef?: any;
  gridTheme?: any;
  setGridReady?: (b: boolean) => void;
  columns?: any[];
  rows?: any[];
  rowsPerPage?: number;
  handleChangePage?: (e?: any) => void;
  sideBar?: any;
  label?: string | null;
  resizable?: boolean;
  minimized?: string | null;
  emitEvent?: (action?: string) => void;
  onChange?: (data?: any) => void;

  /** Preferred for perf: fires once after selection changes settle */
  onSelectionChanged?: (e: any) => void;

  /** Back-compat: per-row selection event (avoid for 32k rows) */
  onRowSelected?: (row?: any) => void;

  selectedItemsData?: any[];
  setSelectedHistoryRow?: (row: any) => void;
  setOpenHistory?: (open: boolean) => void;
  loading?: boolean;
  selectedCount?: number;
};

const RenderTable: React.FC<RenderTableProps> = ({
  gridRef,
  gridTheme,
  setGridReady,
  columns,
  rows,
  rowsPerPage,
  handleChangePage,
  sideBar,
  label,
  resizable,
  minimized,
  emitEvent = () => { },
  onChange,
  onSelectionChanged,
  onRowSelected, // kept for compatibility, but prefer onSelectionChanged
  selectedItemsData,
  setSelectedHistoryRow,
  setOpenHistory,
  loading = false,
  selectedCount = 0,
}) => {
  const selectedItem =
    selectedItemsData?.length > 0 ? selectedItemsData[0] : [];

  const finalColumns = useMemo(() => {
    const selectCol = {
      field: "__select__",
      headerName: "",
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      pinned: "left" as const,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionCurrentPageOnly: true,
      headerCheckboxSelectionFilteredOnly: true,
      suppressHeaderMenuButton: true,
      sortable: false,
      filter: false,
      resizable: false,
      cellStyle: { textAlign: "center", pointerEvents: "auto" } as const,
    };
    return [selectCol, ...(columns ?? [])];
  }, [columns]);

  const LoadingOverlay = () => (
    <Box
      sx={ {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      } }
    >
      <CircularProgress size={ 40 } sx={ { color: "#0a0101" } } />
    </Box>
  );

  const handleSelectionHeaderClick = useCallback((e: any) => {
    const colId = e?.column?.getColId?.();
    const isSelectionHeader =
      colId === "ag-Grid-SelectionColumn" || colId === "__select__";
    if (!isSelectionHeader) return;

    const api = e?.api;
    if (!api) return;

    // Let AG Grid finish header checkbox toggle first.
    setTimeout(() => {
      const currentPage = api.paginationGetCurrentPage?.() ?? 0;
      const pageSize = api.paginationGetPageSize?.() ?? 0;
      const displayedCount = api.getDisplayedRowCount?.() ?? 0;
      const start = currentPage * pageSize;
      const end = Math.min(start + pageSize, displayedCount);

      let selectedOnPage = 0;
      for (let i = start; i < end; i += 1) {
        const node = api.getDisplayedRowAtIndex?.(i);
        if (node?.isSelected?.()) selectedOnPage += 1;
      }

      const totalSelected = api.getSelectedNodes?.().length ?? 0;
      if (totalSelected > 0 && selectedOnPage === 0) {
        api.deselectAll?.();
      }
    }, 0);
  }, []);

  return (
    <ConditionalWrapper
      condition={ minimized !== label }
      elseContent={
        <Box paddingLeft={ 1 }>
          <Typography>
            { label } is minimized, Click here to restore
            <IconButton onClick={ () => emitEvent("") }>
              <ArrowOutwardIcon color="primary" />
            </IconButton>
          </Typography>
        </Box>
      }
    >
      <Stack
        direction="row"
        sx={ {
          minHeight: resizable ? "180px" : null,
          height: resizable ? "250px" : null,
          resize: resizable ? "vertical" : null,
          overflow: resizable ? "auto" : null,
          maxHeight:
            resizable && minimized !== "" && minimized !== label
              ? null
              : resizable
                ? "325px"
                : null,
          flexGrow:
            resizable
              ? null
              : minimized !== "" && minimized !== label
                ? 4
                : 1,
          flexShrink: minimized === label ? 1 : 0,
        } }
      >
        <ConditionalWrapper condition={ !!label }>
          <Stack
            gap={ 2 }
            style={ {
              fontSize: "16px",
              fontWeight: 500,
              transform: "rotate(0deg)",
              backgroundColor: "#fff",
              flex: 0,
              flexDirection: "column-reverse",
              flexWrap: "nowrap",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: 2,
            } }
          >
            <Typography
              sx={ {
                flex: 1,
                writingMode: "sideways-lr",
                textOrientation: "upright",
                textAlign: "center",
                display: "inline-block",
                fontSize: "16px",
                fontWeight: 500,
                padding: "4px 8px",
                whiteSpace: "nowrap",
              } }
            >
              { label }
            </Typography>

            {/* <ConditionalWrapper condition={minimized !== null}>
              <IconButton
                disabled={minimized !== "" && minimized !== label}
                sx={{
                  "&:hover *": {
                    color: theme.palette.primary.main,
                  },
                }}
                onClick={() => emitEvent(label!)}
              >
                <MinimizeIcon />
              </IconButton>
            </ConditionalWrapper> */}
          </Stack>
        </ConditionalWrapper>

        <Box sx={ { flex: 1, position: "relative" } }>
          <AgGridReact
            loading={ loading }
            ref={ gridRef }
            onGridReady={ () => setGridReady?.(true) }
            theme={ gridTheme }
            columnDefs={ finalColumns }
            rowSelection="multiple"
            suppressRowClickSelection={ true }
          getRowId={(params) => String(params.data.__uid)}

            getContextMenuItems={ (params) => {
              const defaultItems = params.defaultItems ?? [];
              return [
                ...defaultItems,
                {
                  name: "View History",
                  action: () => {
                    if (selectedItem) {
                      setSelectedHistoryRow?.(selectedItem);
                      setOpenHistory?.(true);
                    } else {
                      alert("No row selected");
                    }
                  },
                  disabled: !params.node?.data,
                },
              ];
            } }
            enableCharts
            pagination
            paginationPageSize={ rowsPerPage }
            paginationPageSizeSelector={ [5, 10, 20, 50, 100] }
            rowModelType={ "clientSide" }
            rowData={ rows }
            loadingOverlayComponent={ LoadingOverlay }
            overlayNoRowsTemplate={ "<span>No Data Available</span>" }
            onPaginationChanged={ handleChangePage }
            onColumnHeaderClicked={ handleSelectionHeaderClick }
            gridOptions={ { sideBar } }
            /** Prefer this (fires once per change) */
            onSelectionChanged={ onSelectionChanged }
            /** Kept for back-compat if someone still needs per-row event */
            onRowSelected={ onRowSelected }
            stopEditingWhenCellsLoseFocus
            onCellValueChanged={ onChange }
            onCellEditRequest={ (e) => console.log(e.data) }
            singleClickEdit
            suppressMenuHide
            enableCellTextSelection
            suppressRowTransform
            defaultColDef={ {
              sortable: true,
              filter: true,
              resizable: true,
               wrapText: true,
              autoHeight: true,
              cellStyle: {
                whiteSpace: "normal",
                lineHeight: "18px",
               textAlign: "center" },
              valueFormatter: (p: any) => {
                // 1) Skip selection/auto columns to keep checkbox clean
                const isSelectionCol =
                  p.colDef?.checkboxSelection ||
                  p.colDef?.headerCheckboxSelection ||
                  !p.colDef?.field ||
                  p.column?.getColId?.() === "ag-Grid-AutoColumn";
                if (isSelectionCol) return "";

                // 2) Hyphen for empty-like values
                const v = p.value;
                if (
                  v === null ||
                  v === undefined ||
                  (typeof v === "string" && v.trim() === "") ||
                  (typeof v === "number" && Number.isNaN(v))
                ) {
                  return "-";
                }
                return v;
              },
            } }
            data-testid="grid"
          />
          { selectedCount > 0 && (
            <Typography
              sx={ {
                position: "absolute",
                left: 12,
                bottom: 8,
                zIndex: 2,
                fontSize: 13,
                fontWeight: 500,
                color: "text.secondary",
                pointerEvents: "none",
              } }
            >
              <Box component="span" sx={ { fontWeight: 700 } }>
                { selectedCount }
              </Box>{ " " }
              item{ selectedCount !== 1 ? "s" : "" } selected
            </Typography>
          ) }
        </Box>
      </Stack>
    </ConditionalWrapper>
  );
};

/* ---------------------------- EndUserInputView ---------------------------- */

export const EndUserInputView: React.FC = () => {
  const ctx = useEndUserOutletContext();
  const [openChoicePopup, setOpenChoicePopup] = useState(false);
  const [openS4Dialog, setOpenS4Dialog] = useState(false);
  const [selectedForS4, setSelectedForS4] = useState<any[]>([]);
  const [isTriggerFilter, setIsTriggerFilter] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const prevPageRef = useRef<number | null>(null);
  const pendingPageRef = useRef<number | null>(null);
  const suppressPaginationRef = useRef(false);
  const freezeSelectionRef = useRef(false);
  const selectionSnapshotRef = useRef<Set<string>>(new Set());
  const [openPageChangeWarning, setOpenPageChangeWarning] = useState(false);

  // We keep the full selected rows ONLY when needed (on Take Action)
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  // Variance-filtered rows
  const [filteredRows, setFilteredRows] = useState<any[]>([]);
  const [varianceFilterState, setVarianceFilterState] =
    useState<VarianceFilterState | null>(null);
  const [appliedVarianceFilterState, setAppliedVarianceFilterState] =
    useState<VarianceFilterState | null>(null);
  const [varianceFilterResetSignal, setVarianceFilterResetSignal] = useState(0);

  //  Cheap tracking during selection: keep only count + ids in a ref
  const [selectedCount, setSelectedCount] = useState(0);
  const selectedRowIdsRef = useRef<Set<string>>(new Set());
  const dropdownValuesSetRef = useRef(false); // Track if we've set dropdown values
  const dispatch = useDispatch();
  const isSelectionRemove = useSelector(
    (state: any) => state?.supportTicket?.isSelectionRemove ?? false,
  );

  const restoreSelectionSnapshot = useCallback(() => {
    const api = ctx.gridRef?.current?.api;
    if (!api) return;
    const snapshot = selectionSnapshotRef.current;
    if (!snapshot.size) return;

    api.forEachNode((node: any) => {
      const nodeId = String(node.id);
      node.setSelected(snapshot.has(nodeId));
    });
    setSelectedCount(snapshot.size);
  }, [ctx.gridRef]);

  const handleSelectionChanged = useCallback(
    (event: any) => {
      if (freezeSelectionRef.current) return;
      const nodes = event.api.getSelectedNodes();
      selectedRowIdsRef.current = new Set(nodes.map((n: any) => String(n.id)));
      setSelectedCount(selectedRowIdsRef.current.size);
      dispatch(setSelectedRow(nodes.map((n: any) => n.data)));

      // Set default dropdown values only once when rows are first selected
      if (nodes.length > 0 && !dropdownValuesSetRef.current) {
        dropdownValuesSetRef.current = true;
        dispatch(
          setSelectedDropdownValue({
            menuId: "Input View",
            categoryId: "Data",
            functionId: "Data",
          }),
        );
      }

      // Reset the flag when selection is cleared
      if (nodes.length === 0) {
        dropdownValuesSetRef.current = false;
      }
    },
    [dispatch],
  );

  const isTakeActionButtonDisabled = selectedCount < 2;
  const handleTakeActionClick = () => {
    const api = ctx.gridRef?.current?.api;
    if (!api) return;
    const rows = api.getSelectedRows(); // pull full data once, on demand
    if (rows.length < 2) return;
    setSelectedRows(rows);
    dispatch(setSubmittedData(rows));
    setOpenChoicePopup(true);
  };
  const handlePageChange = useCallback(
    (e?: any) => {
      if (suppressPaginationRef.current) return;
      const api = ctx.gridRef?.current?.api;
      if (!api) return;

      const current = api.paginationGetCurrentPage?.() ?? 0;
      const prev = prevPageRef.current;

      // Initialize on first event
      if (prev === null) {
        prevPageRef.current = current;
        return;
      }

      if (current !== prev) {
        if (selectedRowIdsRef.current.size > 0) {
          // Navigate back to previous page and show warning
          freezeSelectionRef.current = true;
          selectionSnapshotRef.current = new Set(selectedRowIdsRef.current);
          suppressPaginationRef.current = true;
          api.paginationGoToPage(prev);
          suppressPaginationRef.current = false;
          pendingPageRef.current = current;
          setOpenPageChangeWarning(true);
        } else {
          api.deselectAll();
          selectedRowIdsRef.current = new Set();
          setSelectedCount(0);
          setSelectedRows([]);
          dropdownValuesSetRef.current = false;
          prevPageRef.current = current;
        }
      }
      // If current === prev, do nothing (don't clear)
    },
    [ctx.gridRef, setSelectedCount],
  );

  const handlePageChangeConfirm = useCallback(() => {
    const api = ctx.gridRef?.current?.api;
    if (!api) return;
    freezeSelectionRef.current = false;
    selectionSnapshotRef.current = new Set();
    api.deselectAll();
    selectedRowIdsRef.current = new Set();
    setSelectedCount(0);
    setSelectedRows([]);
    dropdownValuesSetRef.current = false;
    if (pendingPageRef.current !== null) {
      suppressPaginationRef.current = true;
      api.paginationGoToPage(pendingPageRef.current);
      suppressPaginationRef.current = false;
      prevPageRef.current = pendingPageRef.current;
      pendingPageRef.current = null;
    }
    setOpenPageChangeWarning(false);
  }, [ctx.gridRef]);

  const handlePageChangeCancel = useCallback(() => {
    pendingPageRef.current = null;
    freezeSelectionRef.current = false;
    restoreSelectionSnapshot();
    setOpenPageChangeWarning(false);
  }, [restoreSelectionSnapshot]);
  const handleChoiceSubmit = (
    choice: Choice,
    payloads: any[],
    response?: any,
  ) => {
    if (response?.skippedMaterials?.length > 0) {
      const MAX_ITEMS = 6;
      const items = response.skippedMaterials.slice(0, MAX_ITEMS);
      const remaining = response.skippedMaterials.length - items.length;

      const messageNode = (
        <Box aria-live="polite">
          <Typography variant="body2" sx={ { mb: 0.5 } }>
            { response?.message }
          </Typography>

          <Box component="ul" sx={ { m: 0, pl: 2 } }>
            { items.map((item: any, idx: number) => (
              <li key={ item ?? idx }>
                <Typography variant="body2">{ item ?? "—" }</Typography>
              </li>
            )) }
          </Box>

          { remaining > 0 && (
            <Typography variant="body2" sx={ { mt: 0.5, opacity: 0.8 } }>
              +{ remaining } more…
            </Typography>
          ) }
        </Box>
      );

      enqueueSnackbar({
        message: messageNode,
        severity: "info",
        autoHideDuration: 4000,
      });
    }
    setOpenChoicePopup(false);
    handleChoicePopupClose();
  };
  const DeSelectItems = useCallback(() => {
    const api = ctx.gridRef?.current?.api;
    if (api) api.deselectAll();
    selectedRowIdsRef.current = new Set();
    setSelectedCount(0);
    setSelectedRows([]);
    dropdownValuesSetRef.current = false;
  }, [ctx.gridRef]);

  // Close handler for the Choice popup that also clears selection in the grid
  const handleChoicePopupClose = useCallback(() => {
    setOpenChoicePopup(false);
    DeSelectItems();
  }, [DeSelectItems]);

  useEffect(() => {
    if (openPageChangeWarning) return;
    if (!isSelectionRemove) return;
    DeSelectItems();
  }, [isSelectionRemove, DeSelectItems, openPageChangeWarning]);

  const handleResetFilters = useCallback(() => {
    const api = ctx.gridRef?.current?.api;
    if (!api) return;
    DeSelectItems();
    api.setFilterModel(null);
    if (typeof api.setGridOption === "function") {
      api.setGridOption("quickFilterText", "");
    }
    setVarianceFilterState(null);
    setAppliedVarianceFilterState(null);
    setVarianceFilterResetSignal((prev) => prev + 1);
    setIsTriggerFilter(true);
    api.onFilterChanged();
  }, [ctx.gridRef, DeSelectItems]);

  return (
    <>
      <VarianceFilter
        rows={ ctx.inputRows ?? [] }
        numericField="variationIOvsS4%_numeric"
        columnLabel="Variation IO vs S4 %"
        onFilteredRows={ setFilteredRows }
        onStateChange={setVarianceFilterState}
        appliedState={appliedVarianceFilterState}
        resetSignal={varianceFilterResetSignal}
        actions={
          <>
            {ctx.canSubmitPlanner && (
              <ButtonComponent
                className="take-action"
                type="secondary"
                label="Bulk Action"
                onClick={ handleTakeActionClick }
                disabled={ isTakeActionButtonDisabled }
                endIcon={ <PlayCircleIcon sx={ { fontSize: 16, ml: 0.5 } } /> }
              width="auto"
              maxWidth="none"
              styles={ {
                borderRadius: 28,
              } }
            />
            )}
            {(ctx.inputRows?.length ?? 0) > 0 && (
             <> <ButtonComponent
                className="take-action"
                type="secondary"
                label="Reset Filters"
                onClick={ handleResetFilters }
                width="auto"
                maxWidth="none"
                styles={ {
                  borderRadius: 28,
                } }
              /> 
              <FilterManagerButton    
                             gridRef={ ctx.gridRef }    
                             tableName="Safety Stock Review List-Input View"    
                             userEmail={localStorage.getItem("userEmail") || ""}    
                             disabled={!(ctx.inputRows?.length ?? 0)}
                             appliedFilter={appliedVarianceFilterState}
                             externalFilterState={
                               varianceFilterState
                                 ? {
                                     varianceFilter:
                                       varianceFilterState,
                                   }
                                 : null
                             }
                             onApplyExternalFilterState={
                               (state) => {
                                 const varianceState =
                                   state?.varianceFilter as
                                     | VarianceFilterState
                                     | undefined;
                                 setAppliedVarianceFilterState(
                                   varianceState ||
                                     null,
                                 );
                               }
                             }
                              />
              <BulkPasteFilter
                                gridRef={ ctx.gridRef }
                                columns={ ctx.columns }
                                setIsTriggerFilter={setIsTriggerFilter}
                                triggerClearFilter = {isTriggerFilter}
                              /></>
            )}
            <Download gridRef={ ctx.gridRef } fileName='End User Input View' rowData={ ctx.inputRows } />
          </>
        }
      />
      <Box sx={ { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 } }>
        <RenderTable
        gridRef={ ctx.gridRef }
        columns={ ctx.columns }
        gridTheme={ ctx.gridTheme }
        rows={ filteredRows }
        rowsPerPage={ ctx.rowsPerPage }
        setGridReady={ ctx.setGridReady }
        sideBar={ ctx.sideBar }
        minimized={ null }
        emitEvent={ () => { } }
        onChange={ ctx.handleGridChange }
        selectedItemsData={ ctx.selectedItemsdata }
        setSelectedHistoryRow={ ctx.setSelectedHistoryRow }
        setOpenHistory={ ctx.setOpenHistory }
        onSelectionChanged={ handleSelectionChanged } //  perf-friendly
        loading={ ctx?.loading }
        handleChangePage={ handlePageChange }
        selectedCount={ selectedCount }
      />
      </Box>

      <PopupWithCheckboxes
        open={ openChoicePopup }
        onClose={ () => setOpenChoicePopup(false) }
        onSubmit={ handleChoiceSubmit }
        selectedRows={ selectedRows }
      />

      <S4ReasonTable
        open={ openS4Dialog }
        onClose={ () => setOpenS4Dialog(false) }
        selectedRows={ selectedForS4 }
        onSubmit={ () => {
          setOpenS4Dialog(false);
          setSelectedForS4([]);
        } }
        onCancel={ () => {
          setOpenS4Dialog(false);
          setSelectedForS4([]);
        } }
      />

      <Dialog
        open={ openPageChangeWarning }
        onClose={ handlePageChangeCancel }
        maxWidth="xs"
        fullWidth
        PaperProps={ { sx: { borderRadius: 2, p: 1 } } }
      >
        <DialogTitle sx={ { fontWeight: 600, fontSize: 15, pb: 1 } }>
          Selection
        </DialogTitle>
        <DialogContent sx={ { pt: 0 } }>
          <Typography variant="body2" color="text.secondary">
            You have{ " " }
            <Box component="span" sx={ { fontWeight: 700, color: "text.primary" } }>
              { selectedCount } item{ selectedCount !== 1 ? "s" : "" }
            </Box>{ " " }
            selected. Selections are page-specific and will be cleared when you
            navigate to another page. Do you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions sx={ { px: 2, pb: 2, gap: 1 } }>
          <Button
            variant="outlined"
            size="small"
            onClick={ handlePageChangeCancel }
            sx={ {
              borderRadius: 28,
              textTransform: "none",
              borderColor: "#000",
              color: "#000",
              "&:hover": { borderColor: "#333", backgroundColor: "rgba(0,0,0,0.04)" },
            } }
          >
            Stay
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={ handlePageChangeConfirm }
            sx={ {
              borderRadius: 28,
              textTransform: "none",
              backgroundColor: "#000",
              "&:hover": { backgroundColor: "#333" },
            } }
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
